import type { AssetMetricPoint } from '@src/providers/CoinMetricsApiProvider';
import { useCoinMetricsApiKey, useLazyGetAssetMetricsQuery } from '@src/providers/CoinMetricsApiProvider';
import { logger } from '@talos/kyoko';
import { chunk } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';
import { computePercentChange, fetchAssetMetricsInBatches, parseMetricValue } from './assetLeaderboardMetrics';
import {
  ASSET_LEADERBOARD_1H_FREQUENCY,
  ASSET_LEADERBOARD_1H_FRESHNESS_WINDOW_HOURS,
  ASSET_LEADERBOARD_1H_LOOKBACK,
  ASSET_LEADERBOARD_1H_MAX_GAP_MINUTES,
  ASSET_LEADERBOARD_BATCH_CONCURRENCY,
  ASSET_LEADERBOARD_BATCH_SIZE,
  ASSET_LEADERBOARD_POLL_MS,
  ASSET_LEADERBOARD_PRICE_METRIC,
} from './tokens';

const EMPTY_CHANGE_MAP: ReadonlyMap<string, number> = new Map();

/**
 * A single asset's 1H change plus the timestamp of the hourly point it was computed from. The point
 * time (not the fetch time) is retained so a value carried across failed fetch cycles can be expired
 * against the same freshness window a fresh fetch enforces (see `pruneStaleOneHourChanges`).
 */
export interface OneHourChangeObservation {
  changePct: number;
  /** Epoch ms of the latest hourly point the change was computed from. */
  latestPointTimeMs: number;
}

/**
 * Builds the 1H percent change per asset from hourly `ReferenceRateUSD` points (two per asset,
 * `paging_from: 'end'`). For each asset: the latest point vs the one before it, but only when
 *  - the latest point is within `freshnessWindowHours` of `now` (otherwise the hourly series has gone
 *    stale and there's no meaningful "last hour" move), and
 *  - the two points are no more than `maxGapMinutes` apart, so a thinly-traded asset that skipped an
 *    hour doesn't have a 2h+ move silently reported as "1H".
 * Assets with fewer than two points, a zero baseline, or non-finite values are also omitted. Each
 * kept value carries its latest point's time so it can be expired later. Exported for unit testing.
 */
export function buildOneHourChangeByCmAssetId(
  points: AssetMetricPoint[] | undefined,
  now: Date,
  freshnessWindowHours: number,
  maxGapMinutes: number
): Map<string, OneHourChangeObservation> {
  const pointsByAsset = new Map<string, AssetMetricPoint[]>();
  for (const point of points ?? []) {
    const existing = pointsByAsset.get(point.asset);
    if (existing) {
      existing.push(point);
    } else {
      pointsByAsset.set(point.asset, [point]);
    }
  }

  const freshnessCutoffMs = now.getTime() - freshnessWindowHours * 60 * 60 * 1000;
  const maxGapMs = maxGapMinutes * 60 * 1000;
  const changeByAsset = new Map<string, OneHourChangeObservation>();
  for (const [asset, assetPoints] of pointsByAsset) {
    if (assetPoints.length < 2) {
      continue;
    }
    const sorted = assetPoints.toSorted((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
    const latest = sorted.at(-1)!;
    const prior = sorted.at(-2)!;
    const latestTimeMs = new Date(latest.time).getTime();
    const priorTimeMs = new Date(prior.time).getTime();
    if (!Number.isFinite(latestTimeMs) || !Number.isFinite(priorTimeMs)) {
      continue;
    }
    if (latestTimeMs < freshnessCutoffMs || latestTimeMs - priorTimeMs > maxGapMs) {
      continue;
    }
    const change = computePercentChange(
      parseMetricValue(latest[ASSET_LEADERBOARD_PRICE_METRIC]),
      parseMetricValue(prior[ASSET_LEADERBOARD_PRICE_METRIC])
    );
    if (change != null) {
      changeByAsset.set(asset, { changePct: change, latestPointTimeMs: latestTimeMs });
    }
  }
  return changeByAsset;
}

interface OneHourChangeBatchResult {
  batch: string[];
  /** null when the batch's request failed — distinct from a successful response that computed nothing. */
  changeByAsset: Map<string, OneHourChangeObservation> | null;
}

/**
 * Merges one fetch cycle's per-batch 1H-change results into the running map: a successful batch sets
 * each of its assets to this cycle's observation (or clears it when the batch returned no computable
 * change, so a value that just went stale doesn't linger); a failed batch (`changeByAsset: null`)
 * leaves its assets untouched, so a transient failure keeps the last good value rather than blanking
 * the column. Carry-over is bounded by `pruneStaleOneHourChanges`, not left to linger forever.
 * Mutates and returns `changeByAssetId`. Exported for unit testing.
 */
export function mergeOneHourChangeResults(
  changeByAssetId: Map<string, OneHourChangeObservation>,
  batchResults: OneHourChangeBatchResult[]
): Map<string, OneHourChangeObservation> {
  for (const { batch, changeByAsset } of batchResults) {
    if (changeByAsset == null) {
      continue;
    }
    for (const assetId of batch) {
      const observation = changeByAsset.get(assetId);
      if (observation == null) {
        changeByAssetId.delete(assetId);
      } else {
        changeByAssetId.set(assetId, observation);
      }
    }
  }
  return changeByAssetId;
}

/**
 * Drops carried-over values whose hourly point has aged past `freshnessWindowHours` relative to `now`,
 * using the same cutoff `buildOneHourChangeByCmAssetId` applies to a fresh fetch. A failed batch leaves
 * its assets' last observation in place (see `mergeOneHourChangeResults`); without this, a batch that
 * kept failing would surface a value older than the freshness window — the very staleness a fresh fetch
 * rejects — as the current, sortable "1H" change. Run every cycle (including all-failed cycles) so
 * carry-over is bounded to the same window a successful fetch enforces. Mutates and returns the map.
 * Exported for unit testing.
 */
export function pruneStaleOneHourChanges(
  changeByAssetId: Map<string, OneHourChangeObservation>,
  now: Date,
  freshnessWindowHours: number
): Map<string, OneHourChangeObservation> {
  const freshnessCutoffMs = now.getTime() - freshnessWindowHours * 60 * 60 * 1000;
  for (const [assetId, observation] of changeByAssetId) {
    if (observation.latestPointTimeMs < freshnessCutoffMs) {
      changeByAssetId.delete(assetId);
    }
  }
  return changeByAssetId;
}

/**
 * Fetches the 1H relative price change for the 1h-capable subset of the universe
 * (`cmAssetIdsWith1hPrice`) and returns it keyed by CoinMetrics asset id, for merging onto the daily
 * rows. Batched with bounded concurrency like the daily snapshot, and polled on the same hourly cadence
 * (`ASSET_LEADERBOARD_POLL_MS`) — CoinMetrics only publishes a new `1h` point once an hour, so nothing
 * finer would change the value. Universe-wide (not just visible rows) because the CHANGE column is
 * sortable, so Gainers under 1H needs every asset's value. Values carry across cycles: a failed
 * batch keeps its assets' last good value instead of blanking them (see `mergeOneHourChangeResults`).
 */
/** Reduces the internal observation map to the `{ cmAssetId -> changePct }` shape consumers read. */
function toChangeValueMap(observations: Map<string, OneHourChangeObservation>): Map<string, number> {
  const changeByAssetId = new Map<string, number>();
  for (const [assetId, observation] of observations) {
    changeByAssetId.set(assetId, observation.changePct);
  }
  return changeByAssetId;
}

export function useAssetLeaderboardOneHourChange(
  cmAssetIdsWith1hPrice: readonly string[]
): ReadonlyMap<string, number> {
  const apiKey = useCoinMetricsApiKey();
  const [triggerGetAssetMetrics] = useLazyGetAssetMetricsQuery();
  const [changeByAssetId, setChangeByAssetId] = useState<ReadonlyMap<string, number>>(EMPTY_CHANGE_MAP);
  // Running per-asset observation carried across fetch cycles (see mergeOneHourChangeResults), pruned
  // by freshness each cycle (see pruneStaleOneHourChanges). Reset when the eligible subset itself
  // changes, since old entries wouldn't correspond to the new asset list.
  const changeByAssetIdRef = useRef<Map<string, OneHourChangeObservation>>(new Map());

  // The universe hook hands back a fresh array identity every hourly `now` tick even when membership
  // is unchanged. Keying the batches on a content signature (CoinMetrics ids never contain commas)
  // keeps them referentially stable across those ticks, so an unchanged 1h-eligible set doesn't
  // re-run the effect (which would wipe the carry-over map and refetch) — only a real membership
  // change does; the poll interval alone drives the steady-state hourly refresh.
  const assetIdsSignature = useMemo(() => cmAssetIdsWith1hPrice.join(','), [cmAssetIdsWith1hPrice]);
  const assetIdBatches = useMemo(
    () => (assetIdsSignature === '' ? [] : chunk(assetIdsSignature.split(','), ASSET_LEADERBOARD_BATCH_SIZE)),
    [assetIdsSignature]
  );

  useEffect(() => {
    changeByAssetIdRef.current = new Map();

    if (!apiKey || assetIdBatches.length === 0) {
      setChangeByAssetId(EMPTY_CHANGE_MAP);
      return;
    }

    let cancelled = false;

    async function fetchAllBatches() {
      const batchResults = await fetchAssetMetricsInBatches(
        params => triggerGetAssetMetrics(params).unwrap(),
        assetIdBatches,
        {
          metrics: ASSET_LEADERBOARD_PRICE_METRIC,
          frequency: ASSET_LEADERBOARD_1H_FREQUENCY,
          limit_per_asset: ASSET_LEADERBOARD_1H_LOOKBACK,
        },
        ASSET_LEADERBOARD_BATCH_CONCURRENCY
      );

      if (cancelled) {
        return;
      }

      // 1H is a secondary column and deliberately never errors the board — a failed fetch just leaves
      // those cells blank/last-known. But a *total* failure (every batch down) means the whole 1H
      // column is silently unavailable, which is worth surfacing to telemetry rather than swallowing.
      const failedBatchCount = batchResults.filter(result => result.points == null).length;
      if (batchResults.length > 0 && failedBatchCount === batchResults.length) {
        logger.warn(
          '[asset-leaderboard] 1H change fetch failed for every batch; the 1H column is unavailable this cycle',
          {
            extra: { batchCount: batchResults.length },
          }
        );
      }

      // A failed batch (points: null) stays null here so mergeOneHourChangeResults keeps its assets'
      // last good value; a successful batch is reduced to its per-asset 1H change for this cycle.
      const now = new Date();
      const changeResults = batchResults.map(({ batch, points }) => ({
        batch,
        changeByAsset:
          points == null
            ? null
            : buildOneHourChangeByCmAssetId(
                points,
                now,
                ASSET_LEADERBOARD_1H_FRESHNESS_WINDOW_HOURS,
                ASSET_LEADERBOARD_1H_MAX_GAP_MINUTES
              ),
      }));

      mergeOneHourChangeResults(changeByAssetIdRef.current, changeResults);
      // Expire carry-over that has aged past the freshness window, so a batch that keeps failing can't
      // surface a value older than a fresh fetch would ever accept as the current, sortable 1H change.
      pruneStaleOneHourChanges(changeByAssetIdRef.current, now, ASSET_LEADERBOARD_1H_FRESHNESS_WINDOW_HOURS);
      setChangeByAssetId(toChangeValueMap(changeByAssetIdRef.current));
    }

    fetchAllBatches();
    const intervalId = setInterval(fetchAllBatches, ASSET_LEADERBOARD_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [apiKey, assetIdBatches, triggerGetAssetMetrics]);

  return changeByAssetId;
}
